import Card from '../../components/card/Card';
import Table from '../../components/table/table';
import Icon from '../../components/icon/Icon';
import P from '../../components/texts/p/P';
import Span from '../../components/texts/span/Span';
import Tooltip from '../../components/tooltip/Tooltip';
import { formatCurrency } from '../../utils/Masks';
import { toNumber } from '../../utils/Utils';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { buildResultadosRowsV1 } from './dualBalanceResultados/buildResultadosRowsV1';
import { buildResultadosRowsV2 } from './dualBalanceResultados/buildResultadosRowsV2';
import { ResultadosRow } from './dualBalanceResultados/types';
import {
  getTranslation,
  transformObject,
  sumCategory,
  getMergedValues as getMergedValuesShared,
  DualRow,
} from './balanceShared';

const DUAL_BALANCE_HEADER_BG = '#F5F7FA';
/** Figma: encabezados de período con texto gris oscuro (no rojo) */
const DUAL_BALANCE_HEADER_PERIOD_TEXT = '#231F20';
/** Figma: padding lateral de 16px en primera/última columna (paridad con BalanceSummaryView) */
const TABLE_EDGE_PADDING = '16px';

const ResponsiveTableWrapper = styled.div<{ $hasPrevious: boolean }>`
  width: 100%;
  overflow-x: auto;
  .rdt_Table { min-width: ${({ $hasPrevious }) => ($hasPrevious ? '400px' : '300px')} !important; }
  .rdt_TableCol { min-width: 100px !important; }
  /* Figma: franja gris continua sin líneas blancas entre columnas */
  .rdt_TableHead,
  .rdt_TableHead .rdt_TableHeadRow {
    background-color: ${DUAL_BALANCE_HEADER_BG} !important;
    gap: 0 !important;
  }
  .rdt_TableHead .rdt_TableCol {
    background-color: ${DUAL_BALANCE_HEADER_BG} !important;
    border: none !important;
    border-left: none !important;
    border-right: none !important;
  }
  .rdt_TableHead .rdt_TableCol:nth-child(1) {
    color: ${DUAL_BALANCE_HEADER_PERIOD_TEXT} !important;
    font-weight: 600;
    text-align: left;
  }
  .rdt_TableHead .rdt_TableCol:nth-child(2) {
    color: ${DUAL_BALANCE_HEADER_PERIOD_TEXT} !important;
    font-weight: 600;
    text-align: center;
  }
  .rdt_TableHead .rdt_TableCol:first-child,
  .rdt_TableRow .rdt_TableCell:first-child {
    padding-left: ${TABLE_EDGE_PADDING} !important;
  }
  .rdt_TableHead .rdt_TableCol:last-child,
  .rdt_TableRow .rdt_TableCell:last-child {
    padding-right: ${TABLE_EDGE_PADDING} !important;
  }
  ${({ $hasPrevious }) =>
    $hasPrevious &&
    css`
      .rdt_TableHead .rdt_TableCol:nth-child(3) {
        color: ${DUAL_BALANCE_HEADER_PERIOD_TEXT} !important;
        font-weight: 600;
        text-align: center;
      }
    `}
`;

export interface BalanceDetailTableProps {
  currentRiskData: any;
  currentTotals: any;
  currentPeriodLabel: string;
  previousRiskData?: any;
  previousTotals?: any;
  previousPeriodLabel?: string;
  /** Paso 4 detalle completo: resultados siempre expandidos, sin toggle */
  isDetailView?: boolean;
  isPdfMode?: boolean;
}

const BalanceDetailTable: React.FC<BalanceDetailTableProps> = ({
  previousRiskData,
  previousTotals,
  currentRiskData,
  currentTotals,
  previousPeriodLabel = 'Período anterior',
  currentPeriodLabel,
  isDetailView = false,
  isPdfMode = false,
}) => {
  const hasPrevious = !!previousRiskData && !!previousTotals;
  const config = (() => {
    if (typeof window === 'undefined') return {};
    try {
      return JSON.parse(window.localStorage.getItem('config') ?? '{}');
    } catch {
      return {};
    }
  })();
  const isIncomeStatementV2Enabled = (config as any)?.isRiskStep4IncomeStatementV2Enabled === true;
  const isResultadoBrutoFiltered = (config as any)?.isResultadoBrutoFilteredEnabled ?? false;
  const isComplementaryDataEnabled = (config as any)?.isComplementaryDataEnabled ?? true;
  const isDetailedNetWorth = (config as any)?.isDetailedNetWorthEnabled ?? false;
  const [isResultadosOpen, setIsResultadosOpen] = useState(true);
  const resultadosExpanded = isDetailView || isPdfMode ? true : isResultadosOpen;
  const canToggleResultados =
    isIncomeStatementV2Enabled && !isDetailView && !isPdfMode;

  const toggleResultados = () => {
    if (!canToggleResultados) return;
    setIsResultadosOpen((v) => !v);
  };

  const prevTransformed = previousRiskData ? transformObject(previousRiskData) : [];
  const currTransformed = currentRiskData ? transformObject(currentRiskData) : [];

  const getMergedValues = (category: string) =>
    getMergedValuesShared(currTransformed, prevTransformed, category);

  const getActivoRows = (): DualRow[] => {
    const currentAssets = getMergedValues('current_assets').map((r) => ({
      label: getTranslation('current_assets', r.label),
      valuePrev: r.valuePrev,
      valueCurr: r.valueCurr,
      isBold: false,
    }));
    const nonCurrentAssets = getMergedValues('non_current_assets').map((r) => ({
      label: getTranslation('non_current_assets', r.label),
      valuePrev: r.valuePrev,
      valueCurr: r.valueCurr,
      isBold: false,
    }));
    return [
      { label: 'Activo', valuePrev: previousTotals?.total_current ?? 0, valueCurr: currentTotals?.total_current ?? 0, isBold: true },
      { label: 'Activo corriente', valuePrev: sumCategory(previousRiskData, 'current_assets'), valueCurr: sumCategory(currentRiskData, 'current_assets'), isBold: true },
      ...currentAssets,
      { label: 'Activo no corriente', valuePrev: sumCategory(previousRiskData, 'non_current_assets'), valueCurr: sumCategory(currentRiskData, 'non_current_assets'), isBold: true },
      ...nonCurrentAssets,
    ];
  };

  const getPasivoRows = (): DualRow[] => {
    const currentLiab = getMergedValues('current_liabilities').map((r) => ({
      label: getTranslation('current_liabilities', r.label),
      valuePrev: r.valuePrev,
      valueCurr: r.valueCurr,
      isBold: false,
    }));
    const nonCurrentLiab = getMergedValues('non_current_liabilities').slice(1).map((r) => ({
      label: getTranslation('non_current_liabilities', r.label),
      valuePrev: r.valuePrev,
      valueCurr: r.valueCurr,
      isBold: false,
    }));
    return [
      { label: 'Pasivo', valuePrev: previousTotals?.total_non_current ?? 0, valueCurr: currentTotals?.total_non_current ?? 0, isBold: true },
      { label: 'Pasivo corriente', valuePrev: sumCategory(previousRiskData, 'current_liabilities'), valueCurr: sumCategory(currentRiskData, 'current_liabilities'), isBold: true },
      ...currentLiab,
      { label: 'Pasivo no corriente', valuePrev: sumCategory(previousRiskData, 'non_current_liabilities'), valueCurr: sumCategory(currentRiskData, 'non_current_liabilities'), isBold: true },
      ...nonCurrentLiab,
    ];
  };

  const isPromedioStyle = (row: DualRow) => row.label === 'Resultado del ejercicio';

  const renderResultadosGroupHeaderValue = (row: DualRow) => (
    <Tooltip position='top' msg={String(row.valueCurr)}>
      <Span type='3' variant='neutral' weight='bold'>
        $ {formatCurrency(row.valueCurr ?? 0, true)}
      </Span>
    </Tooltip>
  );

  const renderCurrentValueCell = (row: DualRow) => {
    const isGroupHeader = Boolean((row as any)?.isResultadosGroupHeader);
    if (isGroupHeader) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            width: '100%',
            cursor: canToggleResultados ? 'pointer' : 'default',
          }}
          onClick={toggleResultados}
          role={canToggleResultados ? 'button' : undefined}
          aria-label={canToggleResultados ? 'Toggle Resultados' : undefined}
        >
          {renderResultadosGroupHeaderValue(row)}
          {!hasPrevious && canToggleResultados && (
            <Icon
              name={resultadosExpanded ? 'chevron_up_outline' : 'chevron_down_outline'}
              variant='neutral'
              cursor='pointer'
              width={20}
              height={20}
            />
          )}
        </div>
      );
    }

    return (
      <Tooltip position='top' msg={String(row.valueCurr)}>
        <Span
          type={isPromedioStyle(row) ? '3' : '4'}
          variant={isPromedioStyle(row) ? 'primary' : 'neutral'}
          weight={isPromedioStyle(row) || row.isBold ? 'bold' : 'regular'}
        >
          $ {formatCurrency(row.valueCurr ?? 0, true)}
        </Span>
      </Tooltip>
    );
  };

  // Orden requerido: Actual (ej. 2024) izquierda, Anterior (ej. 2023) derecha
  const getColumns = () => {
    const conceptColumn = {
      name: 'Activo',
      selector: (row: DualRow) =>
        (row as any)?.isResultadosGroupHeader ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: canToggleResultados ? 'pointer' : 'default',
              width: '100%',
            }}
            onClick={toggleResultados}
            role={canToggleResultados ? 'button' : undefined}
            aria-label={canToggleResultados ? 'Toggle Resultados' : undefined}
          >
            <P type='3' variant='neutral' weight='bold'>
              {row.label}
            </P>
          </div>
        ) : (
          <P
            type={isPromedioStyle(row) ? '3' : '4'}
            variant={isPromedioStyle(row) ? 'primary' : 'neutral'}
            weight={isPromedioStyle(row) || row.isBold ? 'bold' : 'regular'}
          >
            {row.label}
          </P>
        ),
      grow: 2,
    };

    const currentColumn = {
      name: currentPeriodLabel,
      selector: (row: DualRow) => renderCurrentValueCell(row),
      right: true,
    };

    if (!hasPrevious) {
      return [conceptColumn, currentColumn];
    }

    return [
      conceptColumn,
      currentColumn,
      {
        name: previousPeriodLabel,
        selector: (row: DualRow) =>
          (row as any)?.isResultadosGroupHeader ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '12px',
                width: '100%',
                cursor: canToggleResultados ? 'pointer' : 'default',
              }}
              onClick={toggleResultados}
              role={canToggleResultados ? 'button' : undefined}
              aria-label={canToggleResultados ? 'Toggle Resultados' : undefined}
            >
              <Tooltip position='top' msg={String(row.valuePrev)}>
                <Span type='3' variant='neutral' weight='bold'>
                  $ {formatCurrency(row.valuePrev ?? 0, true)}
                </Span>
              </Tooltip>
              {canToggleResultados && (
                <Icon
                  name={resultadosExpanded ? 'chevron_up_outline' : 'chevron_down_outline'}
                  variant='neutral'
                  cursor='pointer'
                  width={20}
                  height={20}
                />
              )}
            </div>
          ) : (
            <Tooltip position='top' msg={String(row.valuePrev)}>
              <Span
                type={isPromedioStyle(row) ? '3' : '4'}
                variant={isPromedioStyle(row) ? 'primary' : 'neutral'}
                weight={isPromedioStyle(row) || row.isBold ? 'bold' : 'regular'}
              >
                $ {formatCurrency(row.valuePrev ?? 0, true)}
              </Span>
            </Tooltip>
          ),
        right: true,
      },
    ];
  };

  const netWorthMergedValues = getMergedValues('net_worth');
  const simplifiedNetWorthLabels = ['capital', 'other', 'other_components'];

  const simplifiedNetWorthTotal = (riskData: any) => {
    const nw = riskData?.net_worth;
    if (!nw || typeof nw !== 'object') return 0;
    return toNumber(nw.capital || 0) + toNumber(nw.other || 0);
  };

  const pnTotalPrev = isDetailedNetWorth
    ? (previousTotals?.total_net_worth ?? 0)
    : simplifiedNetWorthTotal(previousRiskData);
  const pnTotalCurr = isDetailedNetWorth
    ? (currentTotals?.total_net_worth ?? 0)
    : simplifiedNetWorthTotal(currentRiskData);

  const patrimonioRows: DualRow[] = [
    { label: 'Patrimonio neto', valuePrev: pnTotalPrev, valueCurr: pnTotalCurr, isBold: true },
    ...netWorthMergedValues
      .filter((r) => isDetailedNetWorth || simplifiedNetWorthLabels.includes(r.label))
      .map((r) => ({
        label: getTranslation('net_worth', r.label),
        valuePrev: r.valuePrev,
        valueCurr: r.valueCurr,
        isBold: false as const,
      })),
  ];

  const totalPnPrev = isDetailedNetWorth
    ? (previousTotals?.total_net_worth_and_non_current ?? 0)
    : (sumCategory(previousRiskData, 'current_liabilities') + sumCategory(previousRiskData, 'non_current_liabilities') + pnTotalPrev + sumCategory(previousRiskData, 'non_controlling_interest_result'));
  const totalPnCurr = isDetailedNetWorth
    ? (currentTotals?.total_net_worth_and_non_current ?? 0)
    : (sumCategory(currentRiskData, 'current_liabilities') + sumCategory(currentRiskData, 'non_current_liabilities') + pnTotalCurr + sumCategory(currentRiskData, 'non_controlling_interest_result'));

  const totalPnRows: DualRow[] = [
    { label: 'Total pasivo + Patrimonio neto', valuePrev: totalPnPrev, valueCurr: totalPnCurr, isBold: true },
  ];
  const resultadosRows: ResultadosRow[] = isIncomeStatementV2Enabled
    ? buildResultadosRowsV2({
        getTranslation,
        previousTotals,
        currentTotals,
        previousRiskData,
        currentRiskData,
        isOpen: resultadosExpanded,
        isResultadoBrutoFiltered,
      })
    : buildResultadosRowsV1({
        getMergedValues,
        getTranslation,
        previousTotals,
        currentTotals,
        previousRiskData,
        currentRiskData,
        isResultadoBrutoFiltered,
      });

  const complementaryDataRows: DualRow[] = (() => {
    if (!isComplementaryDataEnabled) return [];
    const hasCurrPurchases = currentRiskData?.purchases != null;
    const hasPrevPurchases = previousRiskData?.purchases != null;
    const hasCurrAmortDepr = currentRiskData?.amortization_depreciation != null;
    const hasPrevAmortDepr = previousRiskData?.amortization_depreciation != null;
    if (!hasCurrPurchases && !hasPrevPurchases && !hasCurrAmortDepr && !hasPrevAmortDepr) return [];
    const rows: DualRow[] = [
      { label: 'Datos Complementarios', valuePrev: '', valueCurr: '', isBold: true },
    ];
    if (hasCurrPurchases || hasPrevPurchases) {
      rows.push({
        label: 'Compras',
        valuePrev: hasPrevPurchases ? formatCurrency(toNumber(previousRiskData.purchases)) : '',
        valueCurr: hasCurrPurchases ? formatCurrency(toNumber(currentRiskData.purchases)) : '',
      });
    }
    if (hasCurrAmortDepr || hasPrevAmortDepr) {
      rows.push({
        label: 'Amortizaciones y Depreciaciones',
        valuePrev: hasPrevAmortDepr ? formatCurrency(toNumber(previousRiskData.amortization_depreciation)) : '',
        valueCurr: hasCurrAmortDepr ? formatCurrency(toNumber(currentRiskData.amortization_depreciation)) : '',
      });
    }
    return rows;
  })();

  const columns = getColumns();
  const activoRows = getActivoRows();
  const pasivoRows = getPasivoRows();

  const allRows: DualRow[] = [
    ...activoRows,
    ...pasivoRows,
    ...patrimonioRows,
    ...totalPnRows,
    ...resultadosRows,
    ...complementaryDataRows,
  ];

  return (
    <Card>
      <ResponsiveTableWrapper $hasPrevious={hasPrevious}>
        <Table
          columns={columns}
          tableData={{ size: allRows.length, totalElements: allRows.length }}
          data={allRows}
          variant='weak'
          disablePagination
          conditionalRowStyles={
            isIncomeStatementV2Enabled
              ? [
                  {
                    when: (row: any) => Boolean(row?.isResultadosDividerTop),
                    style: {
                      // Línea gruesa arriba del header “Resultados”
                      boxShadow: 'inset 0 2px 0 #4A4A4A',
                    },
                  },
                  {
                    when: (row: any) =>
                      Boolean(row?.isResultadosDividerBottom || row?.isResultadosSubtotal),
                    // Figma: el separador grueso va debajo del subtotal para separar bloques.
                    // Usamos `box-shadow` porque `borderBottom` puede quedar tapado por los bordes
                    // de celdas/rows del DataTable.
                    style: {
                      boxShadow: 'inset 0 -2px 0 #4A4A4A',
                    },
                  },
                ]
              : undefined
          }
        />
      </ResponsiveTableWrapper>
    </Card>
  );
};

export default BalanceDetailTable;
